import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar as CalendarIcon, 
  User, 
  Mail, 
  Phone,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Sparkles
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Cardiac Care",
  "Neurological Assessment",
  "Full Body MRI Screening",
  "Advanced CT Imaging",
  "Anesthesia Services",
  "Pain Management",
  "VIP Executive Screening",
  "Other / Custom Request",
];

const timeSlots = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
];

export default function Appointments() {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    time: "",
    notes: "",
    isVIP: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error("Please fill in all required fields");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return false;
      }
    }
    if (step === 2) {
      if (!formData.service) {
        toast.error("Please select a service");
        return false;
      }
    }
    if (step === 3) {
      if (!date || !formData.time) {
        toast.error("Please select both date and time");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const appointmentData = {
        ...formData,
        date: date ? format(date, "yyyy-MM-dd") : null,
      };
      
      await axios.post(`${API}/appointments`, appointmentData);
      setIsSuccess(true);
      toast.success("Appointment request submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div data-testid="appointments-page" className="min-h-screen pt-24">
        <section className="py-24 md:py-32 bg-vip-black">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="glass rounded-3xl p-12 border border-white/10">
              <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-neon-cyan to-neon-pink flex items-center justify-center">
                <Check className="w-12 h-12 text-black" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">
                Request <span className="gradient-text">Received</span>
              </h1>
              <p className="text-gray-300 text-lg mb-8">
                Thank you for choosing Surgery Center LLC. 
                Our VIP concierge team will contact you within 24 hours to confirm your appointment.
              </p>
              <div className="glass rounded-xl p-6 border border-white/10 text-left mb-8">
                <h3 className="text-neon-cyan text-sm uppercase tracking-wider mb-4">Appointment Details</h3>
                <div className="space-y-2 text-gray-300">
                  <p><span className="text-gray-500">Name:</span> {formData.firstName} {formData.lastName}</p>
                  <p><span className="text-gray-500">Service:</span> {formData.service}</p>
                  <p><span className="text-gray-500">Date:</span> {date ? format(date, "MMMM d, yyyy") : "TBD"}</p>
                  <p><span className="text-gray-500">Time:</span> {formData.time}</p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = "/"}
                data-testid="return-home-btn"
                className="rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-8 py-4"
              >
                Return to Home
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="appointments-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 100%, rgba(191, 64, 191, 0.15) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Book Now</p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter leading-none font-bold italic text-white mb-4">
            VIP <span className="gradient-text">Appointment</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-xl mx-auto">
            Schedule your private consultation with our expert team.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-vip-paper border-y border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Personal Info" },
              { num: 2, label: "Service" },
              { num: 3, label: "Schedule" },
              { num: 4, label: "Confirm" },
            ].map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    data-testid={`step-indicator-${s.num}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                      step >= s.num
                        ? "bg-gradient-to-br from-neon-cyan to-neon-pink text-black"
                        : "bg-white/10 text-gray-500"
                    }`}
                  >
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${
                    step >= s.num ? "text-white" : "text-gray-500"
                  }`}>
                    {s.label}
                  </span>
                </div>
                {index < 3 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                    step > s.num
                      ? "bg-gradient-to-r from-neon-cyan to-neon-pink"
                      : "bg-white/10"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-vip-paper">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
            
            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div data-testid="step-1" className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <User className="w-6 h-6 text-neon-cyan" />
                  <h2 className="font-heading text-2xl text-white">Personal Information</h2>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      First Name *
                    </label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      data-testid="first-name-input"
                      className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Last Name *
                    </label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      data-testid="last-name-input"
                      className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-white placeholder:text-white/30"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    data-testid="email-input"
                    className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-white placeholder:text-white/30"
                  />
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    data-testid="phone-input"
                    className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-white placeholder:text-white/30"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Service Selection */}
            {step === 2 && (
              <div data-testid="step-2" className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <FileText className="w-6 h-6 text-neon-pink" />
                  <h2 className="font-heading text-2xl text-white">Select Service</h2>
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                    Service Type *
                  </label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => handleSelectChange("service", value)}
                  >
                    <SelectTrigger 
                      data-testid="service-select"
                      className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 h-auto focus:ring-0 focus:border-neon-cyan text-white"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-vip-paper border border-white/20">
                      {services.map((service) => (
                        <SelectItem 
                          key={service} 
                          value={service}
                          className="text-white hover:bg-white/10 focus:bg-white/10"
                        >
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                    Additional Notes
                  </label>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any specific requirements or concerns..."
                    rows={4}
                    data-testid="notes-input"
                    className="bg-white/5 border border-white/20 rounded-lg px-4 py-4 focus-visible:ring-0 focus-visible:border-neon-cyan text-white placeholder:text-white/30 resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Date & Time */}
            {step === 3 && (
              <div data-testid="step-3" className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <CalendarIcon className="w-6 h-6 text-royal-purple" />
                  <h2 className="font-heading text-2xl text-white">Select Date & Time</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-4">
                      Preferred Date *
                    </label>
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        data-testid="appointment-calendar"
                        className="rounded-lg border border-white/20 bg-white/5 p-3"
                        classNames={{
                          day_selected: "bg-neon-cyan text-black hover:bg-neon-cyan",
                          day_today: "bg-royal-purple/30 text-white",
                          day: "text-white hover:bg-white/10",
                          head_cell: "text-gray-400",
                          caption: "text-white",
                          nav_button: "text-white hover:bg-white/10",
                        }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-4">
                      Preferred Time *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleSelectChange("time", time)}
                          data-testid={`time-slot-${time.replace(/[:\s]/g, '-')}`}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                            formData.time === time
                              ? "bg-gradient-to-r from-neon-cyan to-neon-pink text-black"
                              : "bg-white/5 text-gray-300 border border-white/20 hover:border-neon-cyan/50"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div data-testid="step-4" className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <Sparkles className="w-6 h-6 text-neon-cyan" />
                  <h2 className="font-heading text-2xl text-white">Confirm Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="glass rounded-xl p-6 border border-white/10">
                    <h3 className="text-neon-cyan text-sm uppercase tracking-wider mb-4">Personal Info</h3>
                    <div className="space-y-2 text-gray-300">
                      <p><span className="text-gray-500">Name:</span> {formData.firstName} {formData.lastName}</p>
                      <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                      {formData.phone && <p><span className="text-gray-500">Phone:</span> {formData.phone}</p>}
                    </div>
                  </div>
                  
                  <div className="glass rounded-xl p-6 border border-white/10">
                    <h3 className="text-neon-pink text-sm uppercase tracking-wider mb-4">Service</h3>
                    <p className="text-white text-lg">{formData.service}</p>
                    {formData.notes && (
                      <p className="text-gray-400 text-sm mt-2">{formData.notes}</p>
                    )}
                  </div>
                  
                  <div className="glass rounded-xl p-6 border border-white/10">
                    <h3 className="text-royal-purple text-sm uppercase tracking-wider mb-4">Schedule</h3>
                    <div className="space-y-2 text-gray-300">
                      <p><span className="text-gray-500">Date:</span> {date ? format(date, "MMMM d, yyyy") : "Not selected"}</p>
                      <p><span className="text-gray-500">Time:</span> {formData.time || "Not selected"}</p>
                    </div>
                  </div>
                </div>

                <p className="text-gray-500 text-sm text-center">
                  By submitting, you agree to our privacy policy and terms of service.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-10 pt-8 border-t border-white/10">
              {step > 1 ? (
                <Button
                  type="button"
                  onClick={prevStep}
                  data-testid="prev-step-btn"
                  variant="outline"
                  className="rounded-full border border-white/20 bg-transparent text-white hover:border-neon-cyan hover:text-neon-cyan px-6 py-4"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 4 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  data-testid="next-step-btn"
                  className="rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-8 py-4"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="submit-appointment-btn"
                  className="rounded-full bg-gradient-to-r from-neon-cyan via-royal-purple to-neon-pink text-white font-bold uppercase tracking-widest px-8 py-4 shadow-[0_0_30px_rgba(191,64,191,0.4)] hover:shadow-[0_0_40px_rgba(191,64,191,0.6)] transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <Sparkles className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
